import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { getInitialPassengers } from 'utils/passengers';
import { getDoc2BrandMap } from 'utils/documentMaps';
import { getStoredPurchaser } from 'utils/storedPurchaser';
import getSafeCountryCallingCode from 'utils/phone';
import { getUserByWalletType } from 'utils/loyalty';

/**
 * useQuickCheckoutPassengersInitialValues hook.
 * Build initialValues for the QuickCheckout passenger form.
 * Reuses getInitialPassengers (preserving ids from already-selected seats).
 * Builds purchaser fields mirroring PassengersContainer.js:185-205.
 * @returns {object} initialValues for the redux-form 'passengers' form.
 */
function useQuickCheckoutPassengersInitialValues() {
  const purchase = usePurchase();
  const env = useWhitelabelEnvs();

  const documentId = useSelector((state) => state.purchase.get('documentId'));
  const documentType = useSelector((state) => state.purchase.get('documentType'));
  const purchaserFirstName = useSelector((state) => state.purchase.get('purchaserFirstName'));
  const purchaserLastName = useSelector((state) => state.purchase.get('purchaserLastName'));
  // eslint-disable-next-line function-paren-newline
  const purchaserSecondLastName = useSelector(
    (state) => state.purchase.get('purchaserSecondLastName'), // prettier-ignore
  );
  const email = useSelector((state) => state.purchase.get('email'));
  const phone = useSelector((state) => state.purchase.get('phone'));
  const phoneCode = useSelector((state) => state.purchase.get('phoneCode'));
  const phoneCountry = useSelector((state) => state.purchase.get('phoneCountry'));
  const addressAttributes = useSelector((state) => state.purchase.get('addressAttributes'));
  const walletType = useSelector((state) => state.purchase.get('walletType'));

  const initialValues = useMemo(() => {
    const { departs, returns, passengerSelection, allowsSeatSelection } = purchase;

    const storedPurchaser = getStoredPurchaser() || {};
    const phoneCountryValue = env.lang?.default?.substring(3) || 'MX';
    const initialPhoneCountry = phoneCountry || storedPurchaser.phoneCountry || phoneCountryValue;
    const initialPhoneCode = getSafeCountryCallingCode(initialPhoneCountry, phoneCode || '');

    const doc2BrandMapPurchaser = getDoc2BrandMap('purchaser');
    const purchaserDocumentsOptions = Object.values(doc2BrandMapPurchaser);

    // Build initial passengers using existing seats (no re-selection)
    const passengersInitialValues = getInitialPassengers({
      departureSelectedSeats: departs.selectedSeats || [],
      returnSelectedSeats: returns?.selectedSeats || [],
      allowsSeatSelection,
      passengerSelection,
      departureTrips: departs.trips || [],
      returnTrips: returns?.trips || [],
      overbooking: false,
    });

    // Derive purchaser fallback from logged-in user or stored data (same as PassengersContainer)
    const user = getUserByWalletType(walletType);

    return {
      documentId: documentId || storedPurchaser.documentId,
      documentType:
        documentType || storedPurchaser.documentType || purchaserDocumentsOptions[0] || 'CC',
      purchaserFirstName:
        purchaserFirstName || storedPurchaser.purchaserFirstName || user?.firstName || '',
      purchaserLastName:
        purchaserLastName || storedPurchaser.purchaserLastName || user?.lastName || '',
      purchaserSecondLastName:
        purchaserSecondLastName || storedPurchaser.purchaserSecondLastName || '',
      email: email || storedPurchaser.email || user?.email || '',
      phone: phone || storedPurchaser.phone || '',
      phoneCountry: initialPhoneCountry,
      phoneCode: initialPhoneCode,
      addressAttributes:
        (addressAttributes && addressAttributes.toJS
          ? addressAttributes.toJS()
          : addressAttributes) ||
        storedPurchaser.addressAttributes ||
        {},
      passengers: passengersInitialValues,
    };
  }, [
    purchase,
    env,
    documentId,
    documentType,
    purchaserFirstName,
    purchaserLastName,
    purchaserSecondLastName,
    email,
    phone,
    phoneCode,
    phoneCountry,
    addressAttributes,
    walletType,
  ]);

  return initialValues;
}

export default useQuickCheckoutPassengersInitialValues;
